import type { Arrangement, Song } from '@/types'
import { nowISO } from '@/lib/utils'

type SongSeed = Omit<Song, 'createdAt' | 'updatedAt'>
type ArrangementSeed = Omit<Arrangement, 'createdAt' | 'updatedAt'>

/**
 * Demo song library.
 *
 * Every text here is either a public-domain hymn or original placeholder
 * writing, so the seed data can ship publicly without licensing concerns.
 * Replace it with your own catalogue via Songs -> New song.
 */
const SONG_SEEDS: SongSeed[] = [
  {
    id: 'song_amazing_grace',
    title: 'Amazing Grace',
    artist: 'Traditional',
    author: 'John Newton',
    ccliNumber: '22025',
    copyright: 'Public Domain',
    bpm: 72,
    originalKey: 'G',
    meter: '3/4',
    themes: ['Grace', 'Salvation', 'Hymn'],
    isPublic: true,
    timesScheduled: 14,
    lyrics: [
      'Verse 1',
      'Amazing grace, how sweet the sound',
      'That saved a wretch like me',
      'I once was lost, but now am found',
      'Was blind but now I see',
      '',
      'Verse 2',
      "'Twas grace that taught my heart to fear",
      'And grace my fears relieved',
      'How precious did that grace appear',
      'The hour I first believed',
    ].join('\n'),
    chordSheet: [
      '{title: Amazing Grace}',
      '{key: G}',
      '',
      '[G]Amazing [G/B]grace how [C]sweet the [G]sound',
      'That [G]saved a wretch like [D]me',
      '[G]I once was [G7]lost but [C]now am [G]found',
      'Was [Em]blind but [D]now I [G]see',
    ].join('\n'),
  },
  {
    id: 'song_come_thou_fount',
    title: 'Come Thou Fount of Every Blessing',
    artist: 'Traditional',
    author: 'Robert Robinson',
    ccliNumber: '108389',
    copyright: 'Public Domain',
    bpm: 82,
    originalKey: 'D',
    meter: '4/4',
    themes: ['Devotion', 'Hymn', 'Grace'],
    isPublic: true,
    timesScheduled: 9,
    lyrics: [
      'Verse 1',
      'Come thou fount of every blessing',
      'Tune my heart to sing thy grace',
      'Streams of mercy never ceasing',
      'Call for songs of loudest praise',
    ].join('\n'),
    chordSheet: [
      '{title: Come Thou Fount}',
      '{key: D}',
      '',
      '[D]Come thou [A]fount of [Bm]every [G]blessing',
      '[D]Tune my [A]heart to [G]sing thy [D]grace',
    ].join('\n'),
  },
  {
    id: 'song_be_thou_my_vision',
    title: 'Be Thou My Vision',
    artist: 'Traditional Irish',
    author: 'Dallán Forgaill, tr. Mary Byrne',
    ccliNumber: '30639',
    copyright: 'Public Domain',
    bpm: 66,
    originalKey: 'Eb',
    meter: '3/4',
    themes: ['Devotion', 'Hymn', 'Surrender'],
    isPublic: true,
    timesScheduled: 6,
    lyrics: [
      'Verse 1',
      'Be thou my vision, O Lord of my heart',
      'Naught be all else to me, save that thou art',
      'Thou my best thought by day or by night',
      'Waking or sleeping, thy presence my light',
    ].join('\n'),
    chordSheet: '{title: Be Thou My Vision}\n{key: Eb}\n\n[Eb]Be thou my [Bb]vision O [Cm]Lord of my [Ab]heart',
  },
  {
    id: 'song_it_is_well',
    title: 'It Is Well With My Soul',
    artist: 'Traditional',
    author: 'Horatio Spafford / Philip Bliss',
    ccliNumber: '25376',
    copyright: 'Public Domain',
    bpm: 68,
    originalKey: 'C',
    meter: '4/4',
    themes: ['Peace', 'Comfort', 'Hymn'],
    isPublic: true,
    timesScheduled: 5,
    lyrics: [
      'Verse 1',
      'When peace like a river attendeth my way',
      'When sorrows like sea billows roll',
      'Whatever my lot, thou hast taught me to say',
      'It is well, it is well with my soul',
    ].join('\n'),
    chordSheet: '{title: It Is Well}\n{key: C}\n\n[C]When peace like a [F]river atten[C]deth my way',
  },
  {
    id: 'song_holy_holy_holy',
    title: 'Holy, Holy, Holy',
    artist: 'Traditional',
    author: 'Reginald Heber',
    ccliNumber: '1156',
    copyright: 'Public Domain',
    bpm: 88,
    originalKey: 'D',
    meter: '4/4',
    themes: ['Adoration', 'Trinity', 'Hymn'],
    isPublic: true,
    timesScheduled: 7,
    lyrics: 'Verse 1\nHoly, holy, holy, Lord God Almighty\nEarly in the morning our song shall rise to thee',
    chordSheet: '{title: Holy Holy Holy}\n{key: D}\n\n[D]Holy, [G]holy, [D]holy',
  },
  {
    id: 'song_blessed_assurance',
    title: 'Blessed Assurance',
    artist: 'Traditional',
    author: 'Fanny Crosby',
    ccliNumber: '22324',
    copyright: 'Public Domain',
    bpm: 96,
    originalKey: 'A',
    meter: '9/8',
    themes: ['Assurance', 'Testimony', 'Hymn'],
    isPublic: false,
    timesScheduled: 3,
    lyrics: 'Verse 1\nBlessed assurance, Jesus is mine\nOh what a foretaste of glory divine',
    chordSheet: '{title: Blessed Assurance}\n{key: A}\n\n[A]Blessed assurance, [D]Jesus is [A]mine',
  },
  {
    id: 'song_doxology',
    title: 'Doxology',
    artist: 'Traditional',
    author: 'Thomas Ken',
    ccliNumber: '56204',
    copyright: 'Public Domain',
    bpm: 74,
    originalKey: 'G',
    meter: '4/4',
    themes: ['Praise', 'Benediction', 'Hymn'],
    isPublic: true,
    timesScheduled: 21,
    lyrics: 'Praise God from whom all blessings flow\nPraise him all creatures here below',
    chordSheet: '{title: Doxology}\n{key: G}\n\n[G]Praise God from [C]whom all [G]blessings flow',
  },
  {
    id: 'song_crown_him',
    title: 'Crown Him With Many Crowns',
    artist: 'Traditional',
    author: 'Matthew Bridges',
    ccliNumber: '23938',
    copyright: 'Public Domain',
    bpm: 104,
    originalKey: 'Bb',
    meter: '4/4',
    themes: ['Majesty', 'Easter', 'Hymn'],
    isPublic: false,
    timesScheduled: 2,
    lyrics: 'Verse 1\nCrown him with many crowns, the Lamb upon his throne',
    chordSheet: '{title: Crown Him}\n{key: Bb}\n\n[Bb]Crown him with [Eb]many [Bb]crowns',
  },
  {
    id: 'song_every_morning_new',
    title: 'Every Morning New',
    artist: 'MDV Miami Worship',
    author: 'MDV Miami Worship Collective',
    copyright: '© MDV Miami Worship — original demo content',
    bpm: 118,
    originalKey: 'A',
    meter: '4/4',
    themes: ['Hope', 'Morning', 'Original'],
    isPublic: true,
    timesScheduled: 11,
    lyrics: [
      'Verse 1',
      'Morning breaks across the hill',
      'Every shadow learning light',
      'All that waited through the night',
      'Wakes and finds you faithful still',
      '',
      'Chorus',
      'Every morning new',
      'Every mercy true',
      'We will rise and sing to you',
    ].join('\n'),
    chordSheet: [
      '{title: Every Morning New}',
      '{key: A}',
      '',
      'Verse',
      '[A]Morning breaks a[E]cross the hill',
      '[F#m]Every shadow [D]learning light',
      '',
      'Chorus',
      '[D]Every [A]morning [E]new',
      '[D]Every [A]mercy [E]true',
    ].join('\n'),
    youtubeUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
  },
  {
    id: 'song_river_of_mercy',
    title: 'River of Mercy',
    artist: 'MDV Miami Worship',
    author: 'MDV Miami Worship Collective',
    copyright: '© MDV Miami Worship — original demo content',
    bpm: 74,
    originalKey: 'Em',
    meter: '6/8',
    themes: ['Mercy', 'Response', 'Original'],
    isPublic: true,
    timesScheduled: 4,
    lyrics: [
      'Verse 1',
      'Deeper than the doubt I carry',
      'Wider than the words I know',
      'There is mercy like a river',
      'And it will not let me go',
    ].join('\n'),
    chordSheet: '{title: River of Mercy}\n{key: Em}\n\n[Em]Deeper than the [C]doubt I carry\n[G]Wider than the [D]words I know',
  },
]

const ARRANGEMENT_SEEDS: ArrangementSeed[] = [
  {
    id: 'arr_amazing_grace_default',
    songId: 'song_amazing_grace',
    name: 'Sunday standard',
    key: 'G',
    bpm: 72,
    sequence: ['Intro', 'Verse 1', 'Verse 2', 'Instrumental', 'Verse 3', 'Outro'],
    lengthSeconds: 300,
    notes: 'Acoustic intro, full band from verse 2.',
    isDefault: true,
  },
  {
    id: 'arr_amazing_grace_acoustic',
    songId: 'song_amazing_grace',
    name: 'Acoustic / communion',
    key: 'D',
    bpm: 64,
    sequence: ['Verse 1', 'Verse 2', 'Verse 3'],
    lengthSeconds: 220,
    notes: 'Voice and guitar only. No drums.',
    isDefault: false,
  },
  {
    id: 'arr_come_thou_fount_default',
    songId: 'song_come_thou_fount',
    name: 'Band arrangement',
    key: 'D',
    bpm: 82,
    sequence: ['Intro', 'Verse 1', 'Verse 2', 'Chorus', 'Verse 3', 'Chorus', 'Outro'],
    lengthSeconds: 330,
    isDefault: true,
  },
  {
    id: 'arr_be_thou_my_vision_default',
    songId: 'song_be_thou_my_vision',
    name: '3/4 hymn',
    key: 'Eb',
    bpm: 66,
    sequence: ['Verse 1', 'Verse 2', 'Verse 3'],
    lengthSeconds: 260,
    isDefault: true,
  },
  {
    id: 'arr_it_is_well_default',
    songId: 'song_it_is_well',
    name: 'Congregational',
    key: 'C',
    bpm: 68,
    sequence: ['Verse 1', 'Chorus', 'Verse 2', 'Chorus', 'Chorus'],
    lengthSeconds: 290,
    isDefault: true,
  },
  {
    id: 'arr_holy_holy_holy_default',
    songId: 'song_holy_holy_holy',
    name: 'Opening hymn',
    key: 'D',
    bpm: 88,
    sequence: ['Verse 1', 'Verse 2', 'Verse 4'],
    lengthSeconds: 240,
    isDefault: true,
  },
  {
    id: 'arr_blessed_assurance_default',
    songId: 'song_blessed_assurance',
    name: 'Standard',
    key: 'A',
    bpm: 96,
    sequence: ['Verse 1', 'Chorus', 'Verse 2', 'Chorus'],
    lengthSeconds: 250,
    isDefault: true,
  },
  {
    id: 'arr_doxology_default',
    songId: 'song_doxology',
    name: 'Sending',
    key: 'G',
    bpm: 74,
    sequence: ['Verse'],
    lengthSeconds: 75,
    notes: 'A cappella first pass, organ on repeat.',
    isDefault: true,
  },
  {
    id: 'arr_crown_him_default',
    songId: 'song_crown_him',
    name: 'Standard',
    key: 'Bb',
    bpm: 104,
    sequence: ['Verse 1', 'Verse 2', 'Verse 3'],
    lengthSeconds: 235,
    isDefault: true,
  },
  {
    id: 'arr_every_morning_new_default',
    songId: 'song_every_morning_new',
    name: 'Full band',
    key: 'A',
    bpm: 118,
    sequence: ['Intro', 'Verse 1', 'Chorus', 'Verse 2', 'Chorus', 'Bridge', 'Chorus', 'Outro'],
    lengthSeconds: 345,
    notes: 'Bridge builds — drums drop out for the first pass.',
    isDefault: true,
  },
  {
    id: 'arr_every_morning_new_lower',
    songId: 'song_every_morning_new',
    name: 'Lower key (G)',
    key: 'G',
    bpm: 116,
    sequence: ['Intro', 'Verse 1', 'Chorus', 'Chorus', 'Outro'],
    lengthSeconds: 280,
    isDefault: false,
  },
  {
    id: 'arr_river_of_mercy_default',
    songId: 'song_river_of_mercy',
    name: 'Response / ministry time',
    key: 'Em',
    bpm: 74,
    sequence: ['Intro', 'Verse 1', 'Chorus', 'Verse 2', 'Chorus', 'Tag'],
    lengthSeconds: 380,
    notes: 'Open-ended tag — watch the worship leader.',
    isDefault: true,
  },
]

export function buildSongs(): Song[] {
  const ts = nowISO()
  return SONG_SEEDS.map((song) => ({ ...song, createdAt: ts, updatedAt: ts }))
}

export function buildArrangements(): Arrangement[] {
  const ts = nowISO()
  return ARRANGEMENT_SEEDS.map((arr) => ({ ...arr, createdAt: ts, updatedAt: ts }))
}
